import React, { useContext, useState } from 'react'
import { StyleSheet, FlatList, View } from 'react-native'
import { BG_STYLES, COMMON_STYLES, Spinner } from '@dehaat/dds'

import { OrderListingScreenProps } from '../routes/types'
import OrderListingCard from '../components/orders/OrderListingCard'
import useFarmerOrders from '../hooks/useFarmerOrders'
import { ResponsiveContext } from 'app/context/ResponsiveUIContext'
import { DIMENSION_WIDTH } from 'app/utils/common'
import SearchInput from '../components/reusable/SearchInput'
import useDebounce from '../hooks/useDebounce'
import FilterButton from '../image-diagnosis/components/filter/FilterButton'
import FilterModal, {
  FORM_INPUT_TYPE,
} from '../image-diagnosis/components/filter/FilterModal'
import { rnNavigation } from 'app/utils/appConfig'
import { ROUTES } from '../routes/types'
import { formatDate } from '../utils/helper'

interface DateFilterRange {
  label: string
  startDate: string
  endDate: string
}

const OrderListing = ({ route }: OrderListingScreenProps) => {
  const { mediaStyles } = useContext(ResponsiveContext)
  const { farmerId } = route.params
  const [searchQuery, setSearchQuery] = useState('')
  const [showFilterModal, setShowFilterModal] = useState(false)
  const [dateFilter, setDateFilter] = useState<DateFilterRange | null>(null)
  const [typeFilter, setTypeFilter] = useState('')

  const debouncedSeachQuery = useDebounce(searchQuery)

  const mapTypeFilter = (id: string): string | null => {
    switch (id) {
      case '1':
        return 'SELF_PICKUP'
      case '2':
        return 'OFFLINE_PICKUP'
      case '3':
        return 'DOORSTEP'
      default:
        return null
    }
  }

  const mappedType = mapTypeFilter(typeFilter)
  const { loading, loadingNext, orders, fetchNext } = useFarmerOrders(
    farmerId,
    dateFilter?.startDate,
    dateFilter?.endDate,
    debouncedSeachQuery,
    mappedType!,
  )

  const computeRange = (label: string): DateFilterRange => {
    const now = new Date()
    const end = new Date(now)
    const start = new Date(now)

    if (label === '7') start.setDate(now.getDate() - 6)
    else if (label === '15') start.setDate(now.getDate() - 14)
    else if (label === '30') start.setDate(now.getDate() - 29)

    return {
      label:
        label === '7'
          ? 'Last 7 days'
          : label === '15'
            ? 'Last 15 days'
            : 'Last 30 days',
      startDate: formatDate(start),
      endDate: formatDate(end),
    }
  }

  const handleFilterApply = (selectedFilters: Record<string, any>) => {
    const selected = selectedFilters?.date_filter

    if (['7', '15', '30'].includes(selected)) {
      setDateFilter(computeRange(selected))
    } else if (
      selected === 'custom' &&
      selectedFilters.fromDate &&
      selectedFilters.toDate
    ) {
      const parseDate = (dateStr: string): Date => {
        const [day, month, year] = dateStr.split('-').map(Number)
        return new Date(year, month - 1, day)
      }
      const start = parseDate(selectedFilters.fromDate)
      const end = parseDate(selectedFilters.toDate)
      setDateFilter({
        label: 'Custom Range',
        startDate: formatDate(start),
        endDate: formatDate(end),
      })
    } else {
      setDateFilter(null)
    }
    setTypeFilter(selectedFilters?.type_filter || '')
  }

  const handleClearFilter = (filterType: 'date' | 'type') => {
    if (filterType === 'date') setDateFilter(null)
    if (filterType === 'type') setTypeFilter('')
  }

  if (loading) {
    return <Spinner type="on-screen" />
  }

  return (
    <View
      style={[
        COMMON_STYLES.row,
        COMMON_STYLES.justifyCenter,
        BG_STYLES.bgWhite,
        COMMON_STYLES.fullHeight,
        COMMON_STYLES.pt1,
        COMMON_STYLES.ph1,
      ]}
    >
      <View
        style={[COMMON_STYLES.flex1, mediaStyles({ desktopStyle: styles.mw })]}
      >
        <SearchInput
          value={searchQuery}
          setValue={setSearchQuery}
          onClear={() => setSearchQuery('')}
          placeholder="Search by name / phone no. / order id"
          containerStyle={COMMON_STYLES['p3/4']}
        />
        <View style={COMMON_STYLES.mb1}>
          <FilterButton
            isFilterActive={!!dateFilter || !!typeFilter}
            onFilterClick={() => setShowFilterModal(true)}
            appliedFilterLabel={[
              dateFilter?.label!,
              mapTypeFilter(typeFilter)
                ?.replaceAll('_', ' ')
                .toLowerCase()
                .replace(/\b\w/g, c => c.toUpperCase())!,
            ]}
            onClearFilter={type => handleClearFilter(type)}
          />
        </View>
        <FlatList
          contentContainerStyle={[
            COMMON_STYLES.row,
            COMMON_STYLES.flexWrap,
            styles['gap1/2'],
          ]}
          data={orders}
          keyExtractor={item => item.id}
          renderItem={({ item }) => (
            <OrderListingCard
              key={item.id}
              orderId={item.id}
              date={item.created_at}
              price={item.grand_total}
              name={item.buyer_full_name}
              orderLines={item.order_lines}
              status={item.status}
              containerStyle={[
                mediaStyles({
                  mobileStyle:
                    DIMENSION_WIDTH < 416
                      ? { width: DIMENSION_WIDTH - 32 }
                      : {},
                }),
              ]}
              onClick={() =>
                rnNavigation({
                  name: ROUTES.ORDER_DETAILS,
                  params: { orderId: item.id },
                })
              }
            />
          )}
          onEndReached={fetchNext}
          ListFooterComponent={
            loadingNext ? <Spinner type={'in-screen'} /> : null
          }
        />
      </View>
      {showFilterModal && (
        <FilterModal
          appliedFilterData={{
            date_filter:
              dateFilter?.label === 'Custom Range'
                ? 'custom'
                : dateFilter?.label?.match(/\d+/)?.[0] || '',
            fromDate: dateFilter?.startDate || '',
            toDate: dateFilter?.endDate || '',
            type_filter: typeFilter || '',
          }}
          onClose={() => setShowFilterModal(false)}
          onApplyFilters={filters => {
            setShowFilterModal(false)
            handleFilterApply(filters)
          }}
          filterList={[
            {
              filterJsonKey: 'date_filter',
              filterName: 'Date',
              inputType: FORM_INPUT_TYPE.DROPDOWN,
              label: 'Select Date',
              filterData: [
                { id: '7', value: 'Last 7 days' },
                { id: '15', value: 'Last 15 days' },
                { id: '30', value: 'Last 30 days' },
                { id: 'custom', value: 'Custom Range' },
              ],
            },
            {
              filterJsonKey: 'type_filter',
              filterName: 'Type',
              inputType: FORM_INPUT_TYPE.DROPDOWN,
              label: 'Select Type',
              filterData: [
                { id: '1', value: 'Self Pickup' },
                { id: '2', value: 'Offline Pickup' },
                { id: '3', value: 'Doorstep' },
              ],
            },
          ]}
        />
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  'gap1/2': {
    gap: 8,
  },
  mw: {
    maxWidth: 1260,
  },
})

export default OrderListing
